import { createRouter, createWebHistory } from "vue-router";
import { useStore } from "vuex";
import { isExcludedPath } from "@/lib/routing";

import companyRoutes from "@/router/companyRoutes";
import permitsRoutes from "@/router/permitsRoutes";
import store from "@/vuex/store";
import accountingRoutes from "./accountingRoutes";
import customersRoutes from "./customersRoutes";
import debugRoutes from "./debugRoutes";
import discountsRoutes from "./discountsRoutes";
import financialsRoutes from "./financialsRoutes";
import jobsRoutes from "./jobsRoutes";
import mapRoutes from "./mapRoutes";
import membershipsRoutes from "./membershipsRoutes";
import programRoutes from "./programRoutes";
import reportsRoutes from "./reportsRoutes";
import scheduleRoutes from "./scheduleRoutes";
import systemRoutes from "./systemRoutes";
import tagsRoutes from "./tagsRoutes";
import todosRoutes from "./todosRoutes";
import Unauthorized from "./Unauthorized.vue";
import workCrewsRoutes from "./workCrewsRoutes";

const unauthorizedRoute = {
  path: "/unauthorized",
  name: "Unauthorized",
  component: Unauthorized
};

const router = createRouter({
  history: createWebHistory(),
  routes: [
    ...customersRoutes,
    ...jobsRoutes,
    ...systemRoutes,
    ...todosRoutes,
    ...financialsRoutes,
    ...workCrewsRoutes,
    ...mapRoutes,
    ...companyRoutes,
    ...reportsRoutes,
    ...discountsRoutes,
    ...permitsRoutes,
    ...scheduleRoutes,
    ...accountingRoutes,
    ...membershipsRoutes,
    ...debugRoutes,
    ...tagsRoutes,
    ...programRoutes,
    unauthorizedRoute
  ]
});

const initClickListener = () => {
  const routingMode = () => {
    return window.routingMode || "Turbo";
  };

  const shouldFullPageLoad = (target, href, prevPath) => {
    if (isExcludedPath(href)) return true;
    if (!isExcludedPath(href) && routingMode() === "Turbo") return true;

    // Default to Vue Router
    return false;
  };

  $(document).on("click", "a", (e) => {
    const target = e.target.closest("a");
    if (!target) return;

    // Let browser handle special links
    const isNewTab = target.target === "_blank";
    const isDownload = target.hasAttribute("download");
    const isExternal = target.href
      ? new URL(target.href).origin !== window.location.origin
      : null;

    if (isNewTab || isDownload || isExternal) return;

    // Allow Rails-specific links to behave normally
    if (
      $(target).data("method") || // rails ujs
      target.hasAttribute("data-turbo-frame") || // turbo link to load content in turbo frame
      target.hasAttribute("data-action") || // bound to stimulus controller action
      target.hasAttribute("data-remote") || // rails ujs
      target.hasAttribute("data-toggle") || // bootstrap trigger
      !target.getAttribute("href") // likely bound to js
    ) {
      return;
    }

    const href = target.href;
    const prevPath = window.history.state?.back || "";

    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      window.open(href);
      return;
    }

    // Required for navigating between Vue/Turbo routing modes to reload page head
    if (shouldFullPageLoad(target, href, prevPath)) {
      e.preventDefault();
      window.location = href;
      return;
    }

    // Otherwise, use Vue Router navigation
    if (href) {
      e.preventDefault();
      const url = new URL(href);
      router.push(url.pathname + url.search);
    }
  });
};
initClickListener();

export const redirectToAuthorizedRoot = (next, permissionKeys) => {
  if (permissionKeys.includes("jobs")) {
    return next();
  }

  if (permissionKeys.includes("customers")) {
    return next("/customers");
  }

  return next("/todos");
};

export const routerGuard = async (to, _from, next) => {
  // If RBAC is NOT enabled -> no need to check for permissions
  if (!window.currentTenant?.feature_flags?.rbac) return next();

  const requiredPermissions = to.meta.permissionRoles as string[] | undefined;
  if (!requiredPermissions?.length) return next();

  const store = useStore();

  await refreshAccessRole(router);

  const permissionKeys = store.getters.permissionKeys || [];

  // Handle root path authorization
  if (to.path === "/") {
    return redirectToAuthorizedRoot(next, permissionKeys);
  }

  const hasPermission = requiredPermissions.some((perm) =>
    permissionKeys.includes(perm)
  );

  if (hasPermission) return next();
  return next({ name: "Unauthorized" });
};

router.beforeEach(routerGuard);

router.afterEach((to, from) => {
  if (to.path !== from.path) {
    store.dispatch("setBreadcrumbs", []);
  }
});

async function refreshAccessRole(router) {
  if (!store.state.accessRole) {
    try {
      await store.dispatch("fetchAccessRole");
    } catch (e) {
      window.notify.error("Failed to load access roles.");
      if (!router.next) return;
      return router.next({ name: "Unauthorized" });
    }
  }
}

export default router;
